import React from 'react';
import { motion } from 'framer-motion';
import Button from '../../../../components/ui/Button';
import Icon from '../../../../components/AppIcon';

const SuccessMessage = ({ email, onTryAgain, onBackToLogin }) => {
  const emailDomain = email.split('@')[1];

  const getEmailProviderLink = (domain) => {
    const providers = {
      'gmail.com': 'https://mail.google.com',
      'yahoo.com': 'https://mail.yahoo.com',
      'outlook.com': 'https://outlook.live.com',
      'hotmail.com': 'https://outlook.live.com',
      'icloud.com': 'https://www.icloud.com/mail',
      'aol.com': 'https://mail.aol.com'
    };
    return providers[domain] || null;
  };

  const providerLink = getEmailProviderLink(emailDomain);

  return (
    <div className="min-h-screen bg-gradient-to-br from-success-50 to-primary-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, type: "spring", stiffness: 200 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img
              src="/assets/images/Giftsy-Logo-Final-1751671984839.png"
              alt="Giftsy Modern"
              className="h-16 w-auto"
            />
          </div>
        </div>

        {/* Success Card */}
        <div className="bg-white rounded-xl shadow-soft p-6 text-center">
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, duration: 0.3, type: "spring", stiffness: 300 }}
            className="inline-flex items-center justify-center w-16 h-16 bg-success text-white rounded-full mb-6"
          >
            <Icon name="Mail" size={32} />
          </motion.div>

          {/* Success Message */}
          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.3 }}
            className="text-xl font-heading font-bold text-accent-800 mb-2"
          >
            Check Your Email
          </motion.h1>

          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.3 }}
            className="text-accent-600 text-sm mb-4"
          >
            We've sent a password reset link to:
          </motion.p>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.3 }}
            className="bg-accent-50 rounded-lg p-3 mb-6"
          >
            <p className="text-accent-800 font-medium text-sm break-all">
              {email}
            </p>
          </motion.div>

          {/* Instructions */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.3 }}
            className="text-left space-y-2 mb-6"
          >
            <h3 className="text-sm font-medium text-accent-800 mb-2">
              Next Steps:
            </h3>
            <div className="space-y-1 text-xs text-accent-600">
              <div className="flex items-start space-x-2">
                <span className="text-primary font-medium">1.</span>
                <span>Check your email inbox for our message</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-primary font-medium">2.</span>
                <span>Click the reset link in the email</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-primary font-medium">3.</span>
                <span>Create a new secure password</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-primary font-medium">4.</span>
                <span>Sign in with your new password</span>
              </div>
            </div>
          </motion.div>

          {/* Quick Access Button */}
          {providerLink && (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.3 }}
              className="mb-4"
            >
              <Button
                onClick={() => window.open(providerLink, '_blank')}
                variant="primary"
                size="sm"
                fullWidth
                className="text-sm"
              >
                <Icon name="ExternalLink" size={16} className="mr-2" />
                Open {emailDomain.charAt(0).toUpperCase() + emailDomain.slice(1).replace('.com', '')}
              </Button>
            </motion.div>
          )}

          {/* Action Buttons */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.3 }}
            className="space-y-3"
          >
            <Button
              onClick={onTryAgain}
              variant="outline"
              size="sm"
              fullWidth
              className="text-sm"
            >
              <Icon name="RotateCcw" size={16} className="mr-2" />
              Try Different Email
            </Button>

            <Button
              onClick={onBackToLogin}
              variant="ghost"
              size="sm"
              fullWidth
              className="text-sm"
            >
              ← Back to Login
            </Button>
          </motion.div>
        </div>

        {/* Help Text */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.3 }}
          className="text-center text-xs text-accent-500 max-w-sm mx-auto mt-6"
        >
          <p className="mb-2">
            The reset link will expire in 24 hours for security reasons.
          </p>
          <p>
            Didn't receive the email? Check your spam folder or{' '}
            <a href="/contact" className="text-primary hover:text-primary-600 font-medium">
              contact support
            </a>
            .
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default SuccessMessage;